// src/components/bouquet/BouquetCustomizer.tsx
import React from "react";
import { motion } from "framer-motion";
import { Gift, Flower } from "lucide-react";
import { calculatePetals, formatPetalDisplay } from "../../utils/petals.utils";
import { cn } from "../../utils/helpers";

interface BouquetCustomizerProps {
  amount?: number;
  onAmountChange: (amount: number) => void;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  maxAmount?: number;
}

export const BouquetCustomizer: React.FC<BouquetCustomizerProps> = ({
  amount = 0,
  onAmountChange,
  enabled,
  onEnabledChange,
  maxAmount = 1000000, // 1M KES max
}) => {
  const petals = calculatePetals(amount);

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="bg-white/5 p-5 rounded-2xl border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-xl">
              <Gift size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-black text-sm uppercase tracking-widest">
                Digital Bouquet
              </h3>
              <p className="text-[10px] text-text-tertiary">
                Add money petals to your surprise
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onEnabledChange(!enabled)}
            className={cn(
              "relative w-12 h-6 rounded-full transition-colors",
              enabled ? "bg-primary" : "bg-white/10",
            )}
          >
            <motion.div
              animate={{ x: enabled ? 24 : 4 }}
              className="absolute top-1 w-4 h-4 bg-white rounded-full"
            />
          </button>
        </div>

        {enabled && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Amount Input */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-text-tertiary block mb-2">
                Amount (KES)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  max={maxAmount}
                  value={amount || ""}
                  onChange={(e) => onAmountChange(Number(e.target.value) || 0)}
                  placeholder="Enter amount (minimum 1000)"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:border-primary focus:ring-4 focus:ring-primary/20 transition-all outline-none"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-primary font-bold text-sm">
                  KES
                </span>
              </div>
              <p className="text-[10px] text-text-tertiary mt-1">
                Minimum: 1,000 KES • 1,000 KES = 1 Petal
              </p>
            </div>

            {/* Petal Preview */}
            {amount > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-primary/10 border border-primary/20 rounded-xl p-4 text-center"
              >
                <div className="text-[10px] font-black uppercase tracking-widest text-primary/60 mb-1">
                  Your Bouquet
                </div>
                <div className="text-3xl font-black text-primary flex items-center justify-center gap-2">
                  <Flower size={24} className="text-primary" />
                  {petals}
                </div>
                <div className="text-xs text-primary/80">
                  {formatPetalDisplay(amount)}
                </div>
                <div className="text-[8px] text-primary/40 mt-2">
                  Value: KES {amount.toLocaleString()}
                </div>
              </motion.div>
            )}

            {/* Info Message */}
            {amount === 0 && enabled && (
              <p className="text-center text-[10px] text-yellow-400/80">
                Enter an amount to add petals to your bouquet
              </p>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BouquetCustomizer;
